// Caisses de régie (H4-07) : le financier voit toutes les caisses et en crée (régisseur habilité à
// encaisser) ; un régisseur voit les siennes. Montant des reçus en attente de vérification.
import { Bouton, EtatVide, Lien, PuceEtat, Tableau, type ColonneTableau } from '@cc/ui';
import { Landmark, Plus } from 'lucide-react';
import { useUtilisateurs } from '../../api/requetes';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { nomUtilisateur } from '../administration/PageUtilisateurs';
import { francs, type Caisse } from './donnees';
import { FormulaireAction } from './FormulaireAction';
import { NavigationFinances, useDroitsRecettes } from './NavigationFinances';
import { useCaisses } from './requetes';
import commun from '../../commun/commun.module.css';
import styles from './recettes.module.css';

export function PageCaisses() {
  const droits = useDroitsRecettes();
  const caisses = useCaisses();
  const utilisateurs = useUtilisateurs(droits.modifier);
  const agents = (utilisateurs.data ?? []).filter((u) => u.statut_appartenance === 'active').map((u) => ({ valeur: u.appartenance_id, libelle: nomUtilisateur(u) }));

  const colonnes: ColonneTableau<Caisse>[] = [
    { id: 'libelle', entete: 'Caisse', enTeteLigne: true, masquable: false, valeurTri: (k) => k.libelle, cellule: (k) => <Lien href={`/recettes/caisses/${k.id}`} className={commun.reference}>{k.libelle}</Lien> },
    { id: 'lieu', entete: 'Lieu', valeurTri: (k) => k.lieu, cellule: (k) => k.lieu },
    { id: 'regisseur', entete: 'Régisseur', valeurTri: (k) => k.regisseur, cellule: (k) => (k.regisseur_appartenance_id === droits.moi ? `${k.regisseur} (vous)` : k.regisseur) },
    { id: 'a_verifier', entete: 'À vérifier', numerique: true, valeurTri: (k) => k.a_verifier, cellule: (k) => <span className={`${styles.montant} ${k.a_verifier > 0 ? styles.montantFort : ''}`}>{francs(k.a_verifier)}</span> },
    { id: 'etat', entete: 'État', valeurTri: (k) => (k.active ? 1 : 0), cellule: (k) => <PuceEtat ton={k.active ? 'ok' : 'neutre'} libelle={k.active ? 'Ouverte' : 'Inactive'} /> },
  ];

  return (
    <>
      <EnTetePage
        etiquette="Finances"
        titre="Caisses"
        description="Caisses de régie : reçus provisoires numérotés émis par le régisseur, clôture journalière avec écart et bordereau, vérification par le financier qui transforme chaque reçu en paiement confirmé avec quittance."
        actions={
          droits.modifier ? (
            <FormulaireAction
              libelle="Nouvelle caisse"
              titre="Créer une caisse"
              explication="Le régisseur désigné doit être un agent actif habilité à encaisser (rôle de régisseur de recettes, droit caisse : créer)."
              chemin="/recettes/caisses"
              icone={Plus}
              champs={[
                { nom: 'libelle', libelle: 'Libellé', obligatoire: true },
                { nom: 'lieu', libelle: 'Lieu', obligatoire: true },
                agents.length > 0 ? { nom: 'regisseur_appartenance_id', libelle: 'Régisseur', type: 'selection', obligatoire: true, options: agents } : { nom: 'regisseur_appartenance_id', libelle: 'Identifiant d\'appartenance du régisseur', obligatoire: true, aide: 'La liste des agents n\'est pas lisible avec vos droits.' },
              ]}
              succes="Caisse créée."
            />
          ) : null
        }
      />
      <NavigationFinances />
      <EtatRequete chargement={caisses.isPending} erreur={caisses.error} onReessayer={() => void caisses.refetch()} lot="L2-A" fonction="La liste des caisses">
        {(caisses.data ?? []).length === 0 ? (
          <EtatVide titre="Aucune caisse" icone={Landmark}>
            <p>{droits.modifier ? 'Créez une caisse pour un régisseur et un lieu.' : 'Aucune caisse ne vous est confiée.'}</p>
          </EtatVide>
        ) : (
          <Tableau legende="Caisses" colonnes={colonnes} lignes={caisses.data ?? []} cleLigne={(k) => k.id} />
        )}
      </EtatRequete>
      {droits.caisseCreer ? <Bouton variante="discret" petit onClick={() => void caisses.refetch()}>Actualiser</Bouton> : null}
    </>
  );
}
